import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Custom hook for managing loading states with advanced features
 * @param {Object} options - Configuration options
 * @param {number} options.minLoadingTime - Minimum loading time in ms (default: 500)
 * @param {number} options.maxLoadingTime - Maximum loading time in ms (default: 30000)
 * @param {boolean} options.autoTimeout - Auto-timeout after maxLoadingTime (default: true)
 * @returns {Object} Loading state and control functions
 */
export const useLoadingState = (options = {}) => {
  const {
    minLoadingTime = 500,
    maxLoadingTime = 30000,
    autoTimeout = true,
  } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [hasError, setHasError] = useState(false);
  
  const startTimeRef = useRef(null);
  const timeoutRef = useRef(null);
  const progressIntervalRef = useRef(null);

  // Start loading with optional message
  const startLoading = useCallback((message = 'Loading...') => {
    setIsLoading(true);
    setLoadingMessage(message);
    setLoadingProgress(0);
    setHasError(false);
    startTimeRef.current = Date.now();

    // Auto-timeout protection
    if (autoTimeout) {
      timeoutRef.current = setTimeout(() => {
        setHasError(true);
        setLoadingMessage('Loading timeout - please try again');
        setTimeout(() => {
          setIsLoading(false);
        }, 2000);
      }, maxLoadingTime);
    }

    // Simulate progress (optional visual enhancement)
    progressIntervalRef.current = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 90) return prev; // Cap at 90% until manually completed
        return prev + Math.random() * 20;
      });
    }, 200);
  }, [autoTimeout, maxLoadingTime]);

  // Stop loading with minimum time guarantee
  const stopLoading = useCallback(async () => {
    const elapsed = startTimeRef.current ? Date.now() - startTimeRef.current : 0;
    const remainingTime = Math.max(0, minLoadingTime - elapsed);

    // Clear timeouts and intervals
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    // Complete progress
    setLoadingProgress(100);

    // Wait for minimum loading time if needed
    if (remainingTime > 0) {
      await new Promise(resolve => setTimeout(resolve, remainingTime));
    }

    // Small delay for progress completion animation
    setTimeout(() => {
      setIsLoading(false);
      setLoadingProgress(0);
    }, 100);
  }, [minLoadingTime]);

  // Set loading message during loading
  const setMessage = useCallback((message) => {
    if (isLoading) {
      setLoadingMessage(message);
    }
  }, [isLoading]);

  // Manually set progress
  const setProgress = useCallback((progress) => {
    if (isLoading && !hasError) {
      setLoadingProgress(Math.max(0, Math.min(100, progress)));
    }
  }, [isLoading, hasError]);

  // Wrapper for async operations
  const withLoading = useCallback(async (asyncFn, message = 'Loading...') => {
    try {
      startLoading(message);
      const result = await asyncFn();
      await stopLoading();
      return result;
    } catch (error) {
      setHasError(true);
      setLoadingMessage('An error occurred');
      setTimeout(async () => {
        await stopLoading();
      }, 2000);
      throw error;
    }
  }, [startLoading, stopLoading]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
  }, []);

  return {
    isLoading,
    loadingMessage,
    loadingProgress,
    hasError,
    startLoading,
    stopLoading,
    setMessage,
    setProgress,
    withLoading,
  };
};

/**
 * Hook for multiple loading states (e.g., different sections)
 * @param {Array<string>} keys - Array of loading state keys
 * @returns {Object} Object with loading states and control functions
 */
export const useMultipleLoadingStates = (keys = []) => {
  const [loadingStates, setLoadingStates] = useState(
    keys.reduce((acc, key) => ({ ...acc, [key]: false }), {})
  );

  const setLoading = useCallback((key, loading) => {
    setLoadingStates(prev => ({ ...prev, [key]: loading }));
  }, []);

  const startLoading = useCallback((key) => {
    setLoading(key, true);
  }, [setLoading]);

  const stopLoading = useCallback((key) => {
    setLoading(key, false);
  }, [setLoading]);

  const isAnyLoading = Object.values(loadingStates).some(Boolean);
  const isAllLoading = keys.length > 0 && keys.every(key => loadingStates[key]);

  return {
    loadingStates,
    setLoading,
    startLoading,
    stopLoading,
    isAnyLoading,
    isAllLoading,
  };
};

/**
 * Hook for sequential loading states (for multi-step processes)
 * @param {Array<string>} steps - Array of step names
 * @returns {Object} Sequential loading state and controls
 */
export const useSequentialLoading = (steps = []) => {
  const [currentStep, setCurrentStep] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);

  const startSequence = useCallback(() => {
    setIsLoading(true);
    setCurrentStep(0);
    setCompletedSteps([]);
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep(prev => {
      const next = prev + 1;
      if (prev >= 0) {
        setCompletedSteps(completed => [...completed, prev]);
      }
      if (next >= steps.length) {
        setIsLoading(false);
        return -1;
      }
      return next;
    });
  }, [steps.length]);

  const completeSequence = useCallback(() => {
    if (currentStep >= 0) {
      setCompletedSteps(completed => [...completed, currentStep]);
    }
    setCurrentStep(-1);
    setIsLoading(false);
  }, [currentStep]);

  const progress = steps.length > 0 ? ((completedSteps.length + (currentStep >= 0 ? 1 : 0)) / steps.length) * 100 : 0;

  return {
    isLoading,
    currentStep,
    currentStepName: currentStep >= 0 ? steps[currentStep] : null,
    completedSteps,
    progress,
    startSequence,
    nextStep,
    completeSequence,
  };
};

export default useLoadingState;
